import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnChanges,
  HostListener,
} from "@angular/core";
import { SafeResourceUrl } from "@angular/platform-browser";

export interface SelectFileEvent {
  base64: string;
  type: string;
  fileName: string;
  extension: string;
}

@Component({
  selector: "app-upload-file",
  templateUrl: "./upload-file.component.html",
  styleUrls: ["./upload-file.component.scss"],
})
export class UploadFileComponent implements OnChanges {
  @Input() isExhibition = false;
  @Input() showModal = false;
  @Input() selectedImage: string | undefined;
  @Input() selectedPdf: SafeResourceUrl | undefined;
  @Input() selectedExcel: SafeResourceUrl | undefined;
  @Input() allowedExtensions = ["png", "jpeg", "jpg", "pdf"];
  @Input() uploadPromptPrefix = "clique";
  @Input() uploadButtonText = "aqui para selecionar.";
  @Input() allowedFormatsLabel = "formatos validos:";
  /** Layout em linha (ícone + ação), para telas que seguem modelo compacto no Figma. */
  @Input() compactLayout = false;
  @Input() figmaDropzoneLayout = false;
  @Input() AllowedAdvancedExtensions: string[] = [];
  @Input() uploadedFile: boolean;
  @Input() isConsult = false;

  @Output() selectFile = new EventEmitter<string | null>();
  @Output() selectFileWithExtension =
    new EventEmitter<SelectFileEvent | null>();

  @Input() maxFiles: number | undefined; // Máximo de arquivos, indefinido se não for configurado

  @Input() permiteRemover: boolean | false = true;

  showInvalidFileFormat = false;
  showFileSizeExceededError = false;
  fileSizeLimit = 5 * 1024 * 1024;

  showExceededFileLimit = false;

  dragged = false;
  fileName = "";
  currentFile: any;
  isImagePreview = false;
  isPdfPreview = false;
  isGenericFilePreview = false;
  fileTypeLabel = "";
  // aaa = "";

  filesTotal = 0;

  ngOnChanges() {
    const rawSelectedImage = this.selectedImage;
    this.normalizePreviewState();

    const isInvalidRawSource =
      !rawSelectedImage ||
      rawSelectedImage.includes(";base64,null") ||
      rawSelectedImage.includes(";base64,undefined") ||
      rawSelectedImage === "data:image/png;base64,undefined";

    if (isInvalidRawSource) {
      this.selectedImage = undefined;
      this.selectedPdf = undefined;
      this.selectedExcel = undefined;
      this.uploadedFile = undefined;
    }

    if (this.selectedImage?.includes("application/xlsx")) {
      this.selectedExcel = this.selectedImage;
      this.selectedImage = undefined;
      this.isGenericFilePreview = false;
    }
  }

  get getAllowedFormats() {
    return this.allowedExtensions?.reduce(
      (prev, curr) => prev + " | " + curr,
      "",
    );
  }

  updateFilesTotal(newTotal: number): void {
    this.filesTotal = newTotal;
  }

  handleFileSelect(event: any): void {
    this.showInvalidFileFormat = false;
    this.showFileSizeExceededError = false;
    this.showExceededFileLimit = false;

    const files = Array.from<any>(event.target.files);

    if (files && files.length > 0) {
      // Verificar se o limite de arquivos foi atingido
      if (
        this.maxFiles !== undefined &&
        files.length + this.filesTotal > this.maxFiles
      ) {
        console.warn(`Limite de ${this.maxFiles} arquivos excedido.`);
        this.showExceededFileLimit = true; // Sinaliza que o limite foi excedido
        return; // Não processa os arquivos se o limite for excedido
      }

      for (const file of files) {
        this.currentFile = file;

        if (this.isFileExtensionInvalid(file, this.allowedExtensions)) {
          this.showInvalidFileFormat = true;
          break;
        }

        if (this.isFileSizeInvalid(file, this.fileSizeLimit)) {
          this.showFileSizeExceededError = true;
          break;
        }

        this.readFile(file);
      }
    }
  }

  handleDropFile(files: any): void {
    this.dragged = true;
    this.fileName = files[0]?.name;

    if (files && files.length > 0) {
      this.readFile(files[0]);
    }
  }

  async readFile(file: any): Promise<void> {
    const base64 = await this.convertToBase64(file).then(
      (x) => x.split(",")[1],
    );
    const fileName = file.name;
    const extension =
      fileName.lastIndexOf(".") === -1
        ? null
        : fileName.substring(fileName.lastIndexOf(".") + 1);

    this.fileName = fileName;
    this.selectFile.emit(base64);
    this.selectFileWithExtension.emit({
      base64: base64,
      type: file.type,
      fileName: fileName,
      extension: extension,
    });

    this.filesTotal++;
  }

  removeFile(): void {
    this.selectedImage = undefined;
    this.selectedPdf = undefined;
    this.selectedExcel = undefined;
    this.uploadedFile = false;
    this.selectFile.emit(undefined);
    this.selectFileWithExtension.emit({} as any);
    this.dragged = false;
  }

  openModal(): void {
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
  }

  convertToBase64(file: File): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  }

  convertBase64ToFile(
    base64: string,
    fileName: string,
    mimeType: string,
  ): File {
    // Remove data URL prefix if present
    const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;

    // Convert base64 to binary string
    const binaryString = atob(base64Data);

    // Convert binary string to Uint8Array
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create Blob and then File
    const blob = new Blob([bytes], { type: mimeType });
    return new File([blob], fileName, { type: mimeType });
  }

  convertBase64ToBlob(base64: string, mimeType: string): Blob {
    // Remove data URL prefix if present
    const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;

    // Convert base64 to binary string
    const binaryString = atob(base64Data);

    // Convert binary string to Uint8Array
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    // Create and return Blob
    return new Blob([bytes], { type: mimeType });
  }

  errorHandler() {
    this.isImagePreview = false;
    this.isPdfPreview = false;
    this.isGenericFilePreview = true;
    this.uploadedFile = true;
  }

  private normalizePreviewState(): void {
    const source = this.normalizeSource(this.selectedImage);
    this.resetPreviewFlags();

    if (!source) {
      this.selectedImage = undefined;
      this.selectedPdf = undefined;
      this.fileTypeLabel = "";
      return;
    }

    const extension = this.getSourceExtension(source);
    this.fileTypeLabel = extension ? extension.toUpperCase() : "";

    if (this.isPdfSource(source, extension)) {
      this.selectedPdf = source;
      this.selectedImage = undefined;
      this.isPdfPreview = true;
      return;
    }

    this.selectedImage = source;
    this.selectedPdf = undefined;
    this.isImagePreview = this.isImageSource(source, extension);
    this.isGenericFilePreview = !this.isImagePreview;
  }

  private resetPreviewFlags(): void {
    this.isImagePreview = false;
    this.isPdfPreview = false;
    this.isGenericFilePreview = false;
  }

  private normalizeSource(source?: string): string | undefined {
    if (
      !source ||
      source.includes(";base64,null") ||
      source.includes(";base64,undefined")
    ) {
      return undefined;
    }

    const match = source.match(/^data:(application|image)\/([a-zA-Z0-9.+-]+);base64,/i);
    if (!match) return source;

    const extension = match[2].toLowerCase();
    const imageMimeByExtension: Record<string, string> = {
      jpg: "image/jpeg",
      jpeg: "image/jpeg",
      png: "image/png",
      gif: "image/gif",
      webp: "image/webp",
      bmp: "image/bmp",
    };

    const imageMime = imageMimeByExtension[extension];
    if (!imageMime) return source;

    return source.replace(
      /^data:(application|image)\/[a-zA-Z0-9.+-]+;base64,/i,
      `data:${imageMime};base64,`
    );
  }

  private getSourceExtension(source: string): string | null {
    const dataUrlMatch = source.match(/^data:([^;]+);base64,/i)?.[1];
    if (dataUrlMatch?.includes("/")) {
      return dataUrlMatch.split("/")[1].toLowerCase();
    }

    try {
      const pathname = new URL(source).pathname.toLowerCase();
      const extension = pathname.match(/\.([a-z0-9]+)$/)?.[1];
      return extension || null;
    } catch {
      const extension = source.toLowerCase().match(/\.([a-z0-9]+)(?:[?#]|$)/)?.[1];
      return extension || null;
    }
  }

  private isPdfSource(source: string, extension: string | null): boolean {
    return source.toLowerCase().includes("application/pdf") || extension === "pdf";
  }

  private isImageSource(source: string, extension: string | null): boolean {
    if (source.toLowerCase().startsWith("data:image/")) return true;
    return ["jpg", "jpeg", "png", "gif", "webp", "bmp"].includes(extension || "");
  }

  @HostListener("document:keydown", ["$event"])
  onKeyDown($event) {
    if ($event.key === "Escape" || $event.key === "Esc") {
      this.showModal = false;
    }
  }

  private isFileExtensionInvalid = (file: File, allowedTypes: string[]) => {
    if (file) {
      const fileType = file.type.split("/")[1];

      if (this.AllowedAdvancedExtensions.length > 0) {
        return !this.AllowedAdvancedExtensions.includes(fileType);
      }

      return !allowedTypes.includes(fileType);
    }
    return false;
  };

  private isFileSizeInvalid = (file: File, maxSize: number) =>
    file.size > maxSize;
}
